import React, { useState, useEffect, useCallback } from 'react';
import { api, type KeyItem, type KeyType, type StayListItem } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtClock } from '../lib/format';

const REFRESH_MS = 30_000;

type DialogState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'issue';  key: KeyItem }
  | { kind: 'lose';   key: KeyItem }
  | { kind: 'archive';key: KeyItem };

export function KeysScreen() {
  const { user } = useAuth();
  const isManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const [keys, setKeys]       = useState<KeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [dialog,  setDialog]  = useState<DialogState>({ kind: 'none' });
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('—');

  const load = useCallback(async () => {
    try {
      const k = await api.listKeys();
      setKeys(k);
      setError(null);
      setLastRefresh(fmtClock(new Date()));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  async function handleReturn(key: KeyItem) {
    setBusyKey(key.id);
    setError(null);
    try {
      await api.returnKey(key.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  }

  const available = keys.filter(k => !k.isLost && !k.activeIssue).length;
  const issued    = keys.filter(k => !!k.activeIssue).length;
  const lost      = keys.filter(k => k.isLost).length;

  return (
    <div className="keys-screen">
      <div className="keys-header">
        <div>
          <h2>Keys</h2>
          <div className="keys-summary">
            <span className="pill available">{available} available</span>
            <span className="pill issued">{issued} issued</span>
            {lost > 0 && <span className="pill lost">{lost} lost</span>}
            <span className="keys-refresh">Refreshed {lastRefresh}</span>
          </div>
        </div>
        {isManager && (
          <button type="button" className="btn-primary" onClick={() => setDialog({ kind: 'add' })}>
            + Add Key
          </button>
        )}
      </div>

      {error && <div className="keys-error">{error}</div>}

      {loading && keys.length === 0 ? (
        <div className="keys-loading">Loading keys…</div>
      ) : keys.length === 0 ? (
        <div className="keys-empty">
          No keys configured for this property yet.
          {isManager && <div style={{ marginTop: 8 }}>Click <strong>+ Add Key</strong> to create one.</div>}
        </div>
      ) : (
        <table className="keys-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Type</th>
              <th>Room</th>
              <th>Status</th>
              <th>Issued to</th>
              <th>Bond</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => {
              const status = k.isLost ? 'Lost' : (k.activeIssue ? 'Issued' : 'Available');
              const statusClass = k.isLost ? 'lost' : (k.activeIssue ? 'issued' : 'available');
              return (
                <tr key={k.id}>
                  <td className="key-label">{k.label}</td>
                  <td>{k.keyType}</td>
                  <td>
                    {k.room
                      ? `Room ${k.room.number}${k.room.displayName ? ' ' + k.room.displayName : ''}`
                      : <span className="muted">—</span>}
                  </td>
                  <td><span className={`pill ${statusClass}`}>{status}</span></td>
                  <td>
                    {k.activeIssue ? (
                      <div>
                        <div>{k.activeIssue.profile.workingName}</div>
                        {k.activeIssue.stay.bed?.room && (
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Room {k.activeIssue.stay.bed.room.number}{k.activeIssue.stay.bed.room.displayName ? ' ' + k.activeIssue.stay.bed.room.displayName : ''} · {k.activeIssue.stay.bed.label}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {k.activeIssue?.bondAmount
                      ? <span>${k.activeIssue.bondAmount}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {k.activeIssue ? (
                      <>
                        <button
                          type="button"
                          className="btn-small"
                          onClick={() => handleReturn(k)}
                          disabled={busyKey === k.id}
                        >
                          {busyKey === k.id ? '…' : 'Return'}
                        </button>
                        {isManager && (
                          <button
                            type="button"
                            className="btn-small btn-danger"
                            onClick={() => setDialog({ kind: 'lose', key: k })}
                            disabled={busyKey === k.id}
                          >
                            Mark Lost
                          </button>
                        )}
                      </>
                    ) : !k.isLost ? (
                      <>
                        <button
                          type="button"
                          className="btn-small btn-primary"
                          onClick={() => setDialog({ kind: 'issue', key: k })}
                        >
                          Issue
                        </button>
                        {isManager && (
                          <button
                            type="button"
                            className="btn-small"
                            onClick={() => setDialog({ kind: 'archive', key: k })}
                          >
                            Archive
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="muted">No actions</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {dialog.kind === 'add'     && <AddKeyDialog     onClose={() => setDialog({ kind: 'none' })} onSaved={load} />}
      {dialog.kind === 'issue'   && <IssueKeyDialog   keyItem={dialog.key} onClose={() => setDialog({ kind: 'none' })} onSaved={load} />}
      {dialog.kind === 'lose'    && <LoseKeyDialog    keyItem={dialog.key} onClose={() => setDialog({ kind: 'none' })} onSaved={load} />}
      {dialog.kind === 'archive' && <ArchiveKeyDialog keyItem={dialog.key} onClose={() => setDialog({ kind: 'none' })} onSaved={load} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function AddKeyDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState('');
  const [keyType, setKeyType] = useState<KeyType>('ROOM');
  const [roomId, setRoomId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<{ id: string; number: string; displayName: string | null }[]>([]);

  // AddKeyDialog needs rooms list for the Room picker
  useEffect(() => {
    (async () => {
      try {
        const rs = await api.rooms();
        setRooms(rs.map(r => ({ id: r.id, number: r.number, displayName: r.displayName })));
      } catch {/* ignore — dropdown just stays empty */}
    })();
  }, []);

  async function handleSave() {
    if (!label.trim()) { setError('Label is required.'); return; }
    setBusy(true);
    try {
      await api.createKey({
        label: label.trim(),
        keyType,
        roomId: keyType === 'ROOM' && roomId ? roomId : null,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Add Key" onClose={onClose} busy={busy}>
      {error && <div className="dialog-error">{error}</div>}
      <div className="dialog-section">
        <label className="dialog-label">Label *</label>
        <input className="dialog-input" autoFocus value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Master Key 1, Room 3 spare" />
      </div>
      <div className="dialog-section">
        <label className="dialog-label">Type</label>
        <select className="dialog-input" value={keyType} onChange={e => setKeyType(e.target.value as KeyType)}>
          <option value="ROOM">Room key</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      {keyType === 'ROOM' && (
        <div className="dialog-section">
          <label className="dialog-label">Room (optional)</label>
          <select className="dialog-input" value={roomId} onChange={e => setRoomId(e.target.value)}>
            <option value="">— Not tied to a specific room —</option>
            {rooms.map(r => (
              <option key={r.id} value={r.id}>
                Room {r.number}{r.displayName ? ' ' + r.displayName : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="dialog-section">
        <label className="dialog-label">Notes</label>
        <textarea className="dialog-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
      </div>
      <div className="dialog-footer">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={busy || !label.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </DialogShell>
  );
}

function IssueKeyDialog({ keyItem, onClose, onSaved }: { keyItem: KeyItem; onClose: () => void; onSaved: () => void }) {
  const [stays, setReservations] = useState<StayListItem[]>([]);
  const [selectedId, setSelectedId]   = useState('');
  const [bondAmount, setBondAmount]   = useState<string>('');
  const [notes, setNotes]             = useState('');
  const [busy, setBusy]               = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Load every active stay, filter to issuable statuses
        const all = await api.staysList('all');
        const issuable = all.filter(r => r.status === 'CONFIRMED' || r.status === 'CHECKED_IN');
        setReservations(issuable);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSave() {
    if (!selectedId) { setError('Pick a stay.'); return; }
    setBusy(true);
    setError(null);
    try {
      const bond = bondAmount.trim() ? parseFloat(bondAmount) : null;
      if (bond !== null && (isNaN(bond) || bond < 0)) {
        setError('Bond must be a non-negative number.');
        setBusy(false);
        return;
      }
      await api.issueKey(keyItem.id, {
        stayId: selectedId,
        bondAmount: bond,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <DialogShell title={`Issue Key: ${keyItem.label}`} onClose={onClose} busy={busy}>
      {error && <div className="dialog-error">{error}</div>}
      {loading ? (
        <div className="dialog-loading">Loading stays…</div>
      ) : stays.length === 0 ? (
        <div className="dialog-section muted">No active stays to issue this key to.</div>
      ) : (
        <>
          <div className="dialog-section">
            <label className="dialog-label">Issue to stay *</label>
            <select
              className="dialog-input"
              autoFocus
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
            >
              <option value="">— Select stay —</option>
              {stays.map(r => (
                <option key={r.id} value={r.id}>
                  {r.occupantName} · {r.arrivalDate.slice(0,10)} → {r.departureDate.slice(0,10)} · {r.status}
                </option>
              ))}
            </select>
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Bond amount (optional)</label>
            <input
              className="dialog-input"
              type="number"
              step="0.01"
              min="0"
              value={bondAmount}
              onChange={e => setBondAmount(e.target.value)}
              placeholder="e.g. 50"
            />
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Notes</label>
            <textarea className="dialog-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </>
      )}
      <div className="dialog-footer">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={busy || !selectedId}>
          {busy ? 'Issuing…' : 'Issue Key'}
        </button>
      </div>
    </DialogShell>
  );
}

function LoseKeyDialog({ keyItem, onClose, onSaved }: { keyItem: KeyItem; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setBusy(true);
    try {
      await api.loseKey(keyItem.id, reason.trim());
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <DialogShell title={`Mark Lost: ${keyItem.label}`} onClose={onClose} busy={busy}>
      {error && <div className="dialog-error">{error}</div>}
      <div className="dialog-section">
        <div style={{ fontSize: 13, color: '#666' }}>
          This marks the key permanently lost. The active issuance is closed
          and the key cannot be issued again until you unlock it manually.
        </div>
      </div>
      <div className="dialog-section">
        <label className="dialog-label">Reason *</label>
        <textarea className="dialog-input" autoFocus rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Guest reported lost. No bond was taken." />
      </div>
      <div className="dialog-footer">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-danger" onClick={handleSave} disabled={busy || !reason.trim()}>
          {busy ? 'Marking…' : 'Mark Lost'}
        </button>
      </div>
    </DialogShell>
  );
}

function ArchiveKeyDialog({ keyItem, onClose, onSaved }: { keyItem: KeyItem; onClose: () => void; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setBusy(true);
    try {
      await api.archiveKey(keyItem.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <DialogShell title={`Archive Key: ${keyItem.label}`} onClose={onClose} busy={busy}>
      {error && <div className="dialog-error">{error}</div>}
      <div className="dialog-section">
        Archiving removes this key from the active list. It can be restored
        manually later if needed.
      </div>
      <div className="dialog-footer">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={busy}>
          {busy ? 'Archiving…' : 'Archive'}
        </button>
      </div>
    </DialogShell>
  );
}

// Shared dialog shell — minor wrapper around the existing .dialog-* classes
function DialogShell({ title, children, onClose, busy }: { title: string; children: React.ReactNode; onClose: () => void; busy?: boolean }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, busy]);

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dialog" role="dialog" aria-labelledby="dialog-title">
        <div className="dialog-titlebar">
          <span id="dialog-title">{title}</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}
